"use client";

import { type MouseEvent, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type ArticleTocItem = {
  id: string;
  label: string;
};

const HEADER_OFFSET = 112;

export const ArticleTableOfContents = ({
  title,
  items,
  className,
}: {
  title: string;
  items: readonly ArticleTocItem[];
  className?: string;
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const itemIds = items.map((item) => item.id).join(",");

  useEffect(() => {
    const ids = itemIds.split(",").filter(Boolean);

    const readActive = () => {
      let current: string | null = null;

      for (const id of ids) {
        const element = document.getElementById(id);

        if (
          element &&
          element.getBoundingClientRect().top <= HEADER_OFFSET + 1
        ) {
          current = id;
        }
      }

      setActiveId(current);
    };

    let frame: number | null = null;

    const onScroll = () => {
      if (frame !== null) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = null;
        readActive();
      });
    };

    readActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [itemIds]);

  const scrollToId = (id: string) => {
    const target = document.getElementById(id);

    if (!target) {
      return false;
    }

    target.scrollIntoView({ behavior: "smooth", block: "start" });

    return true;
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (!scrollToId(id)) {
      return;
    }

    event.preventDefault();
    window.history.pushState(null, "", `#${id}`);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label={title}
      className={cn(
        "flex flex-col gap-y-6 rounded-xl border border-border p-6 shadow-[0px_2px_3px_rgba(0,0,0,0.08)]",
        className,
      )}
    >
      <p className="font-medium text-text text-xl leading-7">{title}</p>
      <ol className="flex flex-col gap-y-3">
        {items.map((item, index) => {
          const isActive = item.id === activeId;

          return (
            <li key={item.id}>
              <a
                className="group flex items-start gap-x-3 rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                href={`#${item.id}`}
                onClick={(event) => handleClick(event, item.id)}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "shrink-0 text-xl tabular-nums transition-colors group-hover:text-text",
                    isActive ? "font-bold text-text" : "text-text-subtle",
                  )}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  className={cn(
                    "pt-0.5 font-medium text-base underline-offset-4 transition-colors group-hover:text-text",
                    isActive ? "text-text" : "text-text-subtle",
                  )}
                >
                  {item.label}
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
